import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import '@/App.css';
import { Toaster } from 'sonner';
import Header from '@/components/Header';
import Hero from '@/components/Hero';
import MarqueeBanner from '@/components/MarqueeBanner';
import Features from '@/components/Features';
import Itinerary from '@/components/Itinerary';
import Fleet from '@/components/Fleet';
import Gallery from '@/components/Gallery';
import Reviews from '@/components/Reviews';
import HowToBook from '@/components/HowToBook';
import BookingSection from '@/components/BookingSection';
import SEOContent from '@/components/SEOContent';
import FAQ from '@/components/FAQ';
import ContactForm from '@/components/ContactForm';
import Footer from '@/components/Footer';
import SocialProof from '@/components/SocialProof';
import BlogList from '@/components/BlogList';
import BlogArticle from '@/components/BlogArticle';

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname]);
  
  return null;
};

// Scroll to element helper for anchor links
const scrollToElement = (elementId) => {
  setTimeout(() => {
    const element = document.getElementById(elementId);
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  }, 100);
};

// Home Page Component
const HomePage = () => {
  const location = useLocation();
  
  useEffect(() => {
    // Handle hash anchors on home page (e.g., /#booking, /#itinerary)
    if (location.hash) {
      const sectionId = location.hash.replace('#', '');
      scrollToElement(sectionId);
    }
  }, [location.hash]);

  return (
    <>
      <Header />
      <main>
        <Hero />
        <MarqueeBanner />
        <Features />
        <Itinerary />
        <Fleet />
        <Gallery />
        <Reviews />
        <HowToBook />
        <BookingSection />
        <SEOContent />
        <FAQ />
        <ContactForm />
      </main>
      <Footer />
      <SocialProof />
    </>
  );
};

// Blog List Page Component
const BlogPage = () => {
  const navigate = useNavigate();
  
  const handleArticleClick = (slug) => {
    navigate(`/blog/${slug}`);
  };

  return (
    <>
      <Header />
      <BlogList onArticleClick={handleArticleClick} />
      <Footer />
    </>
  );
};

// Blog Article Page Component
const BlogArticlePage = () => {
  return (
    <>
      <Header />
      <BlogArticle />
      <Footer />
    </>
  );
};

function App() {
  return (
    <BrowserRouter>
      <div className="bg-[#FDFBF7]">
        <Toaster
          position="bottom-left"
          toastOptions={{
            style: {
              fontFamily: 'Outfit, sans-serif',
              border: '2px solid #0A0A0A',
              borderRadius: '0.75rem',
              boxShadow: '3px 3px 0px 0px rgba(0,0,0,1)',
            },
          }}
        />
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogArticlePage />} />
          {/* Catch-all route - redirect to home */}
          <Route path="*" element={<HomePage />} />
        </Routes>
      </div>
    </BrowserRouter>
  );
}

export default App;
